import React, { useState } from 'react'
import { withStyles } from '@mui/styles'

import { encodeToBase64 } from '../../../core/utils'
import { useAjaxContext } from '../../../core/ajax'
import { ThemeButton } from '../../../ui'
import { DEFAULT_FILE_SIZE, API_ENDPOINT } from '../constants'
import { fileUploaderStyles } from './file-uploader.styles'
import feedback from '../../../core/feedback'

/**
 * @typedef {object} ErrorResponse
 * @property {string} mssg - El mensaje de error a mostrar al usuario.
 */

/**
 * @typedef {object} SuccessResponse
 * @property {object} data - Los datos devueltos por el servidor tras la subida exitosa del documento.
 */

/**
 * @typedef {object} FileUploaderProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {string} label - El texto que se mostrará en el botón de carga.
 * @property {string[]} accept - Un array de tipos MIME aceptados para la carga (ej. `['application/pdf', 'image/jpeg']`).
 * @property {function(SuccessResponse): void} onSuccess - La función callback que se ejecuta cuando el archivo se sube correctamente. Recibe los datos de respuesta del servidor.
 * @property {function(ErrorResponse): void} onError - La función callback que se ejecuta cuando ocurre un error durante la carga. Recibe un objeto con el mensaje de error.
 * @property {boolean} [disabled] - Si es `true`, el botón de carga estará deshabilitado.
 * @property {number} [maxSize=DEFAULT_FILE_SIZE] - El tamaño máximo permitido del archivo en bytes.
 * @property {string} [type='no_definido'] - El tipo de documento que se está subiendo, usado para clasificar el archivo en el backend.
 * @property {string | number} applicationId - El ID de la solicitud asociada al documento.
 * @property {string | number} [budgetId] - El ID del presupuesto asociado al documento (opcional).
 * @property {string | number} clientId - El ID del cliente asociado al documento.
 * @property {string} id - Un identificador único para el input de archivo, usado para vincular el label con el input.
 */

/**
 * Un componente de carga de archivos que renderiza un botón estilizado para seleccionar y subir archivos al servidor.
 * 
 * Valida el tamaño y el tipo de archivo antes de realizar la carga. Convierte el archivo a Base64
 * y lo envía a través de una petición POST al endpoint configurado. Gestiona estados de carga
 * y proporciona feedback visual al usuario mediante notificaciones.
 * 
 * **Nota:** Este componente requiere acceso al contexto de Ajax (`useAjaxContext`) y al sistema de feedback.
 * 
 * @component
 * @example
 * const MyUploadForm = () => {
 *   const handleSuccess = (response) => {
 *     console.log('Archivo subido con éxito:', response.data);
 *   };
 * 
 *   const handleError = (error) => {
 *     alert(`Error: ${error.mssg}`);
 *   };
 * 
 *   return (
 *     <FileUploader
 *       id="invoice-upload"
 *       label="Subir Factura"
 *       accept={['application/pdf']}
 *       maxSize={5000000} // 5 MB
 *       type="factura"
 *       applicationId="12345"
 *       clientId="67890"
 *       onSuccess={handleSuccess}
 *       onError={handleError}
 *     />
 *   );
 * };
 * 
 * @param {FileUploaderProps} props - Las props del componente FileUploader.
 */
export const FileUploader = withStyles(fileUploaderStyles)(
  ({
    classes,
    label,
    accept,
    onSuccess,
    onError,
    disabled,
    maxSize = DEFAULT_FILE_SIZE,
    type = 'no_definido',
    applicationId,
    budgetId,
    clientId,
    ...props
  }) => {
    const { post } = useAjaxContext()

    const [loading, setLoading] = useState(false)

    const handleChangeUpload = async (e) => {
      setLoading(true)
      const file = e.target.files[0]
      if (file && file.size > maxSize) {
        setLoading(false)
        return onError({ mssg: `Excede el tamaño máximo. Tamaño máximo: ${maxSize / 1000000} MB` })
      } else if (file && !accept.some((acceptedType) => acceptedType === file.type)) {
        setLoading(false)
        return onError({
          mssg: `El formato no es correcto. Solo se aceptan los formatos: ${accept.map(
            (format) => format.split('/')[1]
          )}`,
        })
      }
      const mockErrorData = {
        mssg: 'Problema con la subida del fichero',
      }

      postDocument(file)
        .then(({ data }) => {
          setLoading(false)
          feedback('success', 'Documento subido')
          return onSuccess(data)
        })
        .catch(({ response }) => {
          setLoading(false)
          return onError(response ? response.data : mockErrorData)
        })
    }

    const postDocument = async (file) => {
      try {
        const base64File = await encodeToBase64(file)
        let payload = {
          documento: base64File,
          nombre: file.name.replace(/\.[^/.]+$/, ''),
          tipo: type,
          extension: file.type.split('/')[1],
          cliente_id: parseInt(clientId),
          solicitud_id: applicationId.toString(),
        }
        if (budgetId) payload = { ...payload, presupuesto_id: budgetId.toString() }
        return post(API_ENDPOINT, payload)
      } catch (error) {
        console.log(error)
        return Promise.reject(error)
      }
    }

    return (
      <div>
        <input
          id={`file-upload-${props.id}`}
          type="file"
          accept={accept.join(',')}
          className={classes.input}
          onChange={handleChangeUpload}
          disabled={disabled}
        />
        <label htmlFor={`file-upload-${props.id}`}>
          <ThemeButton component="span" {...{ loading, disabled, ...props }}>
            {label}
          </ThemeButton>
        </label>
      </div>
    )
  }
)
